import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, FileDown, ChevronLeft } from 'lucide-react';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../utils/latexParser';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 소문자 l 쌍을 절대값 기호로 변환
const convertAbsoluteValue = (text) => {
  // l...l 쌍을 찾아서 |...|로 변환 (l 뒤에 바로 영문자가 오는 경우는 제외)
  return text.replace(/l([^l]*?)l/g, (match, inner) => {
    // 내부에 수식 문자가 있으면 절대값으로 판단
    if (/[a-km-zA-KM-Z0-9^+\-*/=]/.test(inner)) {
      return `|${inner}|`;
    }
    return match;
  });
};

// 한글 텍스트와 수식을 분리하여 렌더링하는 컴포넌트
const MathText = ({ text }) => {
  if (!text) return null;
  // 소문자 l 쌍 → 절대값 변환
  const processed = convertAbsoluteValue(text);
  // 한글+조사+공백 구간과 비한글 구간을 분리
  const segments = processed.match(/[가-힣ㄱ-ㅎㅏ-ㅣ\s]+|[^가-힣ㄱ-ㅎㅏ-ㅣ\s]+/g) || [processed];

  return segments.map((seg, i) => {
    const isKorean = /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(seg);
    if (isKorean) {
      return <span key={i}>{seg}</span>;
    }
    // 수식이 아닌 단순 구두점/공백만 있는 경우
    if (/^[\s,.·:;!?'"()（）\[\]{}]+$/.test(seg)) {
      return <span key={i}>{seg}</span>;
    }
    try {
      return <InlineMath key={i} math={seg.trim()} />;
    } catch {
      return <span key={i}>{seg}</span>;
    }
  });
};

const TestRegisterPrintModal = ({
  isOpen,
  onClose,
  treeId,
  treeData,
  fetchWithTokenRefresh,
}) => {
  const { getAccessToken } = useAuth();

  // Step 관리: 'select' | 'loading' | 'preview'
  const [step, setStep] = useState('select');

  // 주소 선택 관련 state
  const [selectedDepth, setSelectedDepth] = useState(4);
  const [checkedItems, setCheckedItems] = useState(new Set());

  // 미리보기 관련 state
  const [pages, setPages] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [printPreviewScale, setPrintPreviewScale] = useState(0.7);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPdfInput, setShowPdfInput] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');

  const previewContainerRef = useRef(null);

  // ──────────────────── 1. 주소 항목 목록 생성 ────────────────────

  const getDepthItems = useCallback(() => {
    if (!treeData) return [];
    const depthKey = `depth${selectedDepth}`;
    const yAxis = treeData[depthKey]?.['Y-axis'];
    if (!yAxis) return [];

    return yAxis.map(item => {
      const pathId = item[2];
      const name = item[1];

      // address 라벨 (breadcrumb)
      const parts = pathId.split('/');
      const labels = [];
      for (let d = 1; d <= selectedDepth; d++) {
        const prefix = parts.slice(0, d + 1).join('/');
        const dItem = treeData[`depth${d}`]?.['Y-axis']?.find(x => x[2] === prefix);
        if (dItem) labels.push(dItem[1]);
      }

      // 문제 수 계산
      let problemCount = 0;
      const fieldProblems = treeData.problems?.field_problems;
      if (fieldProblems) {
        if (selectedDepth === 4) {
          const d4Count = fieldProblems.d4_y_axis?.[pathId];
          if (d4Count) problemCount = d4Count.reduce((a, b) => a + b, 0);
        } else {
          // 상위 depth → 하위 d4들의 합계
          const d4Axis = treeData.depth4?.['Y-axis'] || [];
          d4Axis.forEach(d4 => {
            if (d4[2].startsWith(pathId + '/') || d4[2] === pathId) {
              const d4Count = fieldProblems.d4_y_axis?.[d4[2]];
              if (d4Count) problemCount += d4Count.reduce((a, b) => a + b, 0);
            }
          });
        }
      }

      return { pathId, name, label: labels.join(' > '), problemCount };
    });
  }, [treeData, selectedDepth]);

  const depthItems = getDepthItems();

  const totalSelectedProblems = depthItems
    .filter(item => checkedItems.has(item.pathId))
    .reduce((sum, item) => sum + item.problemCount, 0);

  // 전체 선택 토글
  const handleSelectAll = () => {
    if (checkedItems.size === depthItems.length) {
      setCheckedItems(new Set());
    } else {
      setCheckedItems(new Set(depthItems.map(item => item.pathId)));
    }
  };

  const handleCheck = (pathId) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (next.has(pathId)) next.delete(pathId);
      else next.add(pathId);
      return next;
    });
  };

  // depth 변경 시 체크 초기화
  useEffect(() => {
    setCheckedItems(new Set());
  }, [selectedDepth]);

  // ──────────────────── 2. 선택된 주소를 depth4로 확장 ────────────────────

  const expandToDepth4 = useCallback(() => {
    const d4Axis = treeData?.depth4?.['Y-axis'] || [];
    const d4Paths = new Set();

    checkedItems.forEach(pathId => {
      if (selectedDepth === 4) {
        d4Paths.add(pathId);
      } else {
        d4Axis.forEach(d4 => {
          if (d4[2].startsWith(pathId + '/')) {
            d4Paths.add(d4[2]);
          }
        });
      }
    });

    return Array.from(d4Paths);
  }, [treeData, checkedItems, selectedDepth]);

  // ──────────────────── 3. address 라벨 생성 ────────────────────

  const buildAddressLabel = useCallback((d4PathId) => {
    if (!treeData) return [];
    const parts = d4PathId.split('/');
    const labels = [];
    for (let d = 1; d <= 4; d++) {
      const prefix = parts.slice(0, d + 1).join('/');
      const item = treeData[`depth${d}`]?.['Y-axis']?.find(x => x[2] === prefix);
      if (item) labels.push(item[1]);
    }
    return labels;
  }, [treeData]);

  // ──────────────────── 4. 문제 데이터 가져오기 + 변환 ────────────────────

  const fetchAndPrepareProblems = useCallback(async () => {
    const d4Paths = expandToDepth4();
    if (d4Paths.length === 0) return;

    setStep('loading');
    setLoadingProgress('문제 목록 조회 중...');

    try {
      // 4-1. 각 d4 주소 × step(1-5) 문제 목록 조회
      const tasks = [];
      d4Paths.forEach(d4Path => {
        for (let s = 1; s <= 5; s++) {
          tasks.push({ d4Path, step: s });
        }
      });

      // 동시 10개 제한 병렬 호출
      const problemsByGroup = new Map(); // d4Path → [{problem_id, step}]
      const CONCURRENCY = 10;

      for (let i = 0; i < tasks.length; i += CONCURRENCY) {
        const batch = tasks.slice(i, i + CONCURRENCY);
        const results = await Promise.all(
          batch.map(async ({ d4Path, step }) => {
            try {
              const res = await fetchWithTokenRefresh(
                `/api/referencefields/${treeId}/problems?address=${d4Path}&step=${step}`
              );
              if (res?.status === 'success' && res?.data?.problems) {
                return { d4Path, step, problems: res.data.problems };
              }
            } catch (e) {
              console.error(`Failed to fetch problems for ${d4Path} step ${step}:`, e);
            }
            return { d4Path, step, problems: [] };
          })
        );

        results.forEach(({ d4Path, step, problems }) => {
          if (!problemsByGroup.has(d4Path)) problemsByGroup.set(d4Path, []);
          if (Array.isArray(problems)) {
            problems.forEach(p => {
              const pid = p.problem_id || p;
              problemsByGroup.get(d4Path).push({ problem_id: pid, step });
            });
          }
        });

        setLoadingProgress(`문제 목록 조회 중... (${Math.min(i + CONCURRENCY, tasks.length)}/${tasks.length})`);
      }

      // 4-2. 고유 problem_id 수집
      const allProblemIds = new Set();
      problemsByGroup.forEach(list => list.forEach(p => allProblemIds.add(p.problem_id)));

      if (allProblemIds.size === 0) {
        alert('선택한 주소에 등록된 문제가 없습니다.');
        setStep('select');
        return;
      }

      setLoadingProgress(`문제 콘텐츠 조회 중... (${allProblemIds.size}개)`);

      // 4-3. 문제 콘텐츠 일괄 조회
      const idsArray = Array.from(allProblemIds);
      const BATCH_SIZE = 50;
      const contentMap = new Map(); // problem_id → content html

      for (let i = 0; i < idsArray.length; i += BATCH_SIZE) {
        const batchIds = idsArray.slice(i, i + BATCH_SIZE);
        try {
          const res = await fetchWithTokenRefresh(
            `/api/problems/print?problem_ids=${batchIds.join(',')}`
          );
          if (res?.data && Array.isArray(res.data)) {
            res.data.forEach(problem => {
              const lexicalData = problem?.lexical_data?.lexical_data;
              if (!lexicalData) return;

              const editor = createEditor({ nodes: [...PlaygroundNodes] });

              const processNode = (node) => {
                if (node.type === 'equation') {
                  node.equation = processMathEquation(node.equation);
                } else if (node.type === 'box-content') {
                  node.className = 'PlaygroundEditorTheme__boxContent';
                }
                if (node.children && Array.isArray(node.children)) {
                  node.children = node.children.map(child => processNode(child));
                }
                return node;
              };

              lexicalData.root = processNode(lexicalData.root);
              const editorState = editor.parseEditorState(lexicalData);

              let content = '';
              editor.setEditorState(editorState);
              editor.update(() => {
                content = $generateHtmlFromNodes(editor);
              });

              contentMap.set(problem.problem_id, content);
            });
          }
        } catch (e) {
          console.error('Failed to fetch problem content batch:', e);
        }
        setLoadingProgress(`문제 콘텐츠 조회 중... (${Math.min(i + BATCH_SIZE, idsArray.length)}/${idsArray.length})`);
      }

      // 4-4. 그룹화 및 정렬
      setLoadingProgress('페이지 배분 중...');

      // d4 path를 숫자 기준 정렬
      const sortedD4Paths = Array.from(problemsByGroup.keys()).sort((a, b) => {
        const pa = a.split('/').map(Number);
        const pb = b.split('/').map(Number);
        for (let i = 0; i < pa.length; i++) {
          if (pa[i] !== pb[i]) return pa[i] - pb[i];
        }
        return 0;
      });

      const groups = sortedD4Paths.map(d4Path => {
        const problems = (problemsByGroup.get(d4Path) || [])
          .filter(p => contentMap.has(p.problem_id))
          .sort((a, b) => a.step - b.step)
          .map(p => ({
            problem_id: p.problem_id,
            content: contentMap.get(p.problem_id),
            step: p.step,
            addressLabel: buildAddressLabel(d4Path),
          }));

        return {
          groupPath: d4Path,
          groupLabel: buildAddressLabel(d4Path),
          problems,
        };
      }).filter(g => g.problems.length > 0);

      // 4-5. 페이지 배분
      const distributed = distributeGroupedProblems(groups);
      setPages(distributed);
      setStep('preview');
    } catch (error) {
      console.error('문제 데이터 준비 실패:', error);
      alert('문제 데이터를 준비하는 중 오류가 발생했습니다.');
      setStep('select');
    }
  }, [expandToDepth4, fetchWithTokenRefresh, treeId, buildAddressLabel]);

  // ──────────────────── 5. 높이 계산 ────────────────────

  const calculateProblemHeightWithLabel = (problem, number) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: 488px;
      left: -9999px;
      display: block;
      opacity: 0;
    `;

    tempDiv.innerHTML = `
      <div style="margin-bottom: 6mm;">
        <div style="font-size: 10px; color: #666; background: #f5f5f5; padding: 2mm 3mm; border-radius: 1rem; display: block; width: 100%; height: 97px; box-sizing: border-box;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0 6px; height: 100%; align-content: center;">
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <span style="font-size: 8px; font-weight: bold; color: #A3A1A2;">대단원</span>
              <span style="font-size: 12px; color: #231F20;">${problem.addressLabel[0] || ''}</span>
            </div>
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <span style="font-size: 8px; font-weight: bold; color: #A3A1A2;">소단원</span>
              <span style="font-size: 12px; color: #231F20;">${problem.addressLabel[1] || ''}</span>
            </div>
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <span style="font-size: 8px; font-weight: bold; color: #A3A1A2;">대유형</span>
              <span style="font-size: 12px; color: #231F20;">${problem.addressLabel[2] || ''}</span>
            </div>
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <span style="font-size: 8px; font-weight: bold; color: #A3A1A2;">중유형</span>
              <span style="font-size: 12px; color: #231F20;">${problem.addressLabel[3] || ''}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="problem-item" style="display: flex; gap: 28px; align-items: flex-start; position: static;">
        <div class="problem-number" style="display: block;">${String(number).padStart(2, '0')}</div>
        <div class="problem-content" style="display: block;">${problem.content}</div>
      </div>
    `;

    document.body.appendChild(tempDiv);
    const height = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);

    return height / 3.779527559055118; // px → mm
  };

  // ──────────────────── 6. 문제 배분 알고리즘 ────────────────────

  const distributeGroupedProblems = (groups) => {
    const allPages = [];
    let problemCounter = 0;
    // 모든 페이지 동일: print-header-rest(15mm) + 문제 영역 410.5mm
    const MID_HEIGHT = 205; // 칼럼 상/하 분할점
    const SINGLE_AREA = 205; // 칼럼 반쪽 높이

    groups.forEach((group, groupIdx) => {
      // 첫 그룹이 아니면 반드시 새 페이지에서 시작
      if (groupIdx > 0) {
        // 이전 그룹 잔여 페이지 push는 아래에서 처리됨
      }
      let currentPage = { left: [], right: [] };

      group.problems.forEach(problem => {
        problemCounter++;
        const problemHeight = calculateProblemHeightWithLabel(problem, problemCounter);

        // 페이지가 가득 찼는지 확인
        const isPageFull =
          (currentPage.left.length === 2 && currentPage.right.length === 2) ||
          (currentPage.left.length === 1 && currentPage.left[0]?.useFullColumn && currentPage.right.length === 2) ||
          (currentPage.right.length === 1 && currentPage.right[0]?.useFullColumn);

        if (isPageFull) {
          allPages.push({ ...currentPage, groupLabel: group.groupLabel });
          currentPage = { left: [], right: [] };
        }

        const curMid = MID_HEIGHT;
        const curSingle = SINGLE_AREA;

        const problemData = {
          ...problem,
          problemNumber: problemCounter,
          height: problemHeight,
        };

        if (problemHeight > curSingle) {
          problemData.useFullColumn = true;
          problemData.area = 'full';
          problemData.startHeight = 0;

          if (currentPage.left.length === 0 && currentPage.right.length === 0) {
            problemData.column = 'left';
            currentPage.left = [problemData];
          } else if (currentPage.left.length >= 1 && currentPage.right.length === 0) {
            problemData.column = 'right';
            currentPage.right = [problemData];
          } else {
            allPages.push({ ...currentPage, groupLabel: group.groupLabel });
            currentPage = { left: [{ ...problemData, column: 'left' }], right: [] };
          }
          return;
        }

        const addToColumn = (column) => {
          const colArray = column === 'left' ? currentPage.left : currentPage.right;
          const isTop = colArray.length === 0;
          problemData.startHeight = isTop ? 0 : curMid;
          problemData.column = column;
          colArray.push(problemData);
        };

        if (currentPage.left.length === 1 && currentPage.left[0].useFullColumn) {
          if (currentPage.right.length < 2) addToColumn('right');
        } else if (currentPage.right.length === 1 && currentPage.right[0].useFullColumn) {
          allPages.push({ ...currentPage, groupLabel: group.groupLabel });
          currentPage = { left: [], right: [] };
          addToColumn('left');
        } else {
          if (currentPage.left.length < 2) addToColumn('left');
          else if (currentPage.right.length < 2) addToColumn('right');
        }
      });

      if (currentPage.left.length > 0 || currentPage.right.length > 0) {
        allPages.push({ ...currentPage, groupLabel: group.groupLabel });
      }
    });

    return allPages;
  };

  // ──────────────────── 7. 인쇄 / PDF ────────────────────

  const generatePrintHtml = useCallback(() => {
    const printContent = document.querySelector('.tr-preview-container');
    if (!printContent) return null;

    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .filter(rule => {
              if (rule.type === CSSRule.FONT_FACE_RULE) {
                return !rule.cssText.includes('../');
              }
              return true;
            })
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    return `
      <html>
        <head>
          <title>Print</title>
          <style>
            ${styles}

            @page {
              size: A4;
              margin: 0;
            }

            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              width: 328mm !important;
              height: 464mm !important;
              font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif !important;
            }

            * {
              font-family: inherit;
            }

            .tr-preview-container {
              width: 328mm !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              zoom: 1 !important;
              position: relative !important;
            }

            .tr-page {
              width: 328mm !important;
              min-height: 464mm !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              position: relative !important;
              page-break-after: always !important;
            }

            .problems-container {
              padding: 0 16mm !important;
              display: flex !important;
              flex-direction: row !important;
              gap: 16mm !important;
              justify-content: space-between !important;
              position: relative !important;
            }

            .problems-container {
              height: 410.5mm !important;
              min-height: 410.5mm !important;
              max-height: 410.5mm !important;
              margin-top: 57px !important;
              padding: 0 60.4724px !important;
            }

            .problem-content *:not(.katex):not(.katex *) {
              font-family: 'Pretendard', sans-serif !important;
              font-weight: 400 !important;
              font-size: 18px !important;
            }

            .katex {
              font-size: 1em !important;
            }

            @media print {
              html, body {
                width: 328mm !important;
                height: 464mm !important;
              }
              .tr-preview-container {
                width: 328mm !important;
              }
              .no-print {
                display: none !important;
              }
            }
          </style>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css">
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.css">
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            (async () => {
              const warnings = [];
              const READY_FORCE_TIMEOUT_MS = 8000;
              const FONTS_TIMEOUT_MS = 3000;
              const IMAGES_TIMEOUT_MS = 4000;

              const setReady = () => {
                if (warnings.length > 0) {
                  window.__PDF_READY_WARN__ = warnings.join(',');
                }
                window.__PDF_READY__ = true;
              };

              const forceTimer = setTimeout(() => {
                warnings.push('ready_forced_timeout=' + READY_FORCE_TIMEOUT_MS);
                setReady();
              }, READY_FORCE_TIMEOUT_MS);

              try {
                if (document.fonts && document.fonts.ready) {
                  const fontsOk = await Promise.race([
                    document.fonts.ready.then(() => true).catch(() => false),
                    new Promise((res) => setTimeout(() => res(false), FONTS_TIMEOUT_MS)),
                  ]);
                  if (!fontsOk) warnings.push('fonts_timeout=' + FONTS_TIMEOUT_MS);
                }
              } catch (e) {
                warnings.push('fonts_error');
              }

              try {
                const imgs = Array.from(document.images || []);
                let failedImages = 0;
                const waitImages = Promise.allSettled(
                  imgs.map((img) =>
                    img.complete
                      ? Promise.resolve()
                      : new Promise((res) => {
                          img.addEventListener("load", res, { once: true });
                          img.addEventListener("error", () => { failedImages += 1; res(); }, { once: true });
                        })
                  )
                );
                const imagesOk = await Promise.race([
                  waitImages.then(() => true).catch(() => false),
                  new Promise((res) => setTimeout(() => res(false), IMAGES_TIMEOUT_MS)),
                ]);
                if (!imagesOk) warnings.push('images_timeout=' + IMAGES_TIMEOUT_MS);
                if (failedImages > 0) warnings.push('images_failed=' + failedImages);
              } catch (e) {
                warnings.push('images_error');
              }

              clearTimeout(forceTimer);
              setReady();
            })();
          </script>
        </body>
      </html>
    `;
  }, []);

  const handlePrint = useCallback(() => {
    const html = generatePrintHtml();
    if (!html) return;

    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 3000);
  }, [generatePrintHtml]);

  // 이미지 URL → Base64 변환
  const convertImageToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('이미지 변환 실패:', url, error);
      return url;
    }
  };

  const convertAllImagesToBase64 = async (html) => {
    const baseUrl = window.location.origin;
    let processedHtml = html
      .replace(/url\(["']?(\/[^"')]+)["']?\)/g, `url("${baseUrl}$1")`)
      .replace(/src=["']?(\/[^"'\s>]+)["']?/g, `src="${baseUrl}$1"`)
      .replace(/href=["']?(\/[^"'\s>]+)["']?/g, `href="${baseUrl}$1"`);

    const imageUrls = new Set();
    const urlPattern = /url\(["']?(https?:\/\/[^"')]+\.(?:png|jpg|jpeg|gif|svg|webp))["']?\)/gi;
    let match;
    while ((match = urlPattern.exec(processedHtml)) !== null) imageUrls.add(match[1]);
    const srcPattern = /src=["'](https?:\/\/[^"']+\.(?:png|jpg|jpeg|gif|svg|webp))["']/gi;
    while ((match = srcPattern.exec(processedHtml)) !== null) imageUrls.add(match[1]);

    for (const url of imageUrls) {
      const base64 = await convertImageToBase64(url);
      if (base64 !== url) processedHtml = processedHtml.split(url).join(base64);
    }

    processedHtml = processedHtml.replace(/url\(["']?\.\.\/[^"')]+["']?\)/g, 'url("")');
    return processedHtml;
  };

  const handleDownloadPdf = useCallback(async () => {
    if (isGeneratingPdf || pages.length === 0) return;

    setIsGeneratingPdf(true);
    try {
      const html = generatePrintHtml();
      if (!html) throw new Error('미리보기 HTML을 가져올 수 없습니다.');

      const finalHtml = await convertAllImagesToBase64(html);
      const token = await getAccessToken();
      if (!token) throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');

      const defaultName = `문제출력_${new Date().toISOString().slice(0, 10)}`;
      const filename = (pdfFilename.trim() || defaultName) + '.pdf';

      const htmlBlob = new Blob([finalHtml], { type: 'text/html;charset=utf-8' });
      const htmlFile = new File([htmlBlob], 'document.html', { type: 'text/html' });

      const formData = new FormData();
      formData.append('html_file', htmlFile);
      formData.append('filename', filename);
      formData.append('encoding', 'utf-8');

      const response = await fetch(`${API_BASE_URL}/api/pdfs/generate-upload`, {
        method: 'POST',
        headers: { 'Authorization': token },
        body: formData,
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        throw new Error(`서버가 바쁩니다. ${retryAfter ? `${retryAfter}초 후` : '잠시 후'} 다시 시도해주세요.`);
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let serverMessage = '';
        if (contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          serverMessage = errorData.message || '';
        } else {
          serverMessage = (await response.text().catch(() => '')).slice(0, 300);
        }
        throw new Error(serverMessage || `PDF 생성 실패: ${response.status}`);
      }

      const result = await response.json();
      const downloadUrl = result.data?.download_url;
      if (!downloadUrl) throw new Error('다운로드 URL을 받지 못했습니다.');

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('PDF 다운로드 오류:', error);
      alert(`PDF 다운로드 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [isGeneratingPdf, pages.length, generatePrintHtml, getAccessToken, pdfFilename]);

  // ──────────────────── 8. 렌더링 ────────────────────

  if (!isOpen) return null;

  const depthTitles = { 1: '대단원', 2: '소단원', 3: '대유형', 4: '중유형' };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 9999980 }}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>

      <div className="relative z-10 flex w-[95vw] flex-col overflow-hidden rounded-3xl bg-white shadow-lg lg:w-[960px]"
        style={{ height: '90vh', maxHeight: '90vh' }}
      >
        {/* ───── 헤더 ───── */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-3xl border-b bg-white p-4">
          <div className="flex items-center gap-2">
            {step === 'preview' && (
              <button
                onClick={() => setStep('select')}
                className="rounded-full p-1 hover:bg-gray-200"
                title="주소 선택으로 돌아가기"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-bold text-gray-800">
              {step === 'select' ? '출력 범위 선택' : step === 'loading' ? '준비 중...' : '문제 출력 미리보기'}
            </h1>
          </div>
          <div className="flex items-center">
            {step === 'preview' && (
              <>
                {/* 축소/확대/초기화 */}
                <div className="mr-4 flex items-center gap-2">
                  <button
                    onClick={() => setPrintPreviewScale(s => Math.max(0.2, s - 0.1))}
                    disabled={printPreviewScale <= 0.2}
                    className="rounded-lg bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    −
                  </button>
                  <span className="min-w-[40px] rounded bg-gray-100 px-2 py-1 text-center text-xs font-medium">
                    {Math.round(printPreviewScale * 100)}%
                  </span>
                  <button
                    onClick={() => setPrintPreviewScale(s => Math.min(1.2, s + 0.1))}
                    disabled={printPreviewScale >= 1.2}
                    className="rounded-lg bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    +
                  </button>
                  <button
                    onClick={() => setPrintPreviewScale(0.7)}
                    className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    초기화
                  </button>
                </div>

                {/* PDF 다운로드 */}
                <div className="relative mr-2">
                  <button
                    onClick={() => setShowPdfInput(prev => !prev)}
                    disabled={isGeneratingPdf || pages.length === 0}
                    className="rounded-full p-1 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                    title="PDF 다운로드"
                  >
                    {isGeneratingPdf ? (
                      <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                    ) : (
                      <FileDown size={20} />
                    )}
                  </button>
                  {showPdfInput && (
                    <div className="absolute right-0 top-full z-20 mt-1 flex items-center gap-1 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-200">
                      <input
                        type="text"
                        value={pdfFilename}
                        onChange={(e) => setPdfFilename(e.target.value)}
                        placeholder="문제출력"
                        className="w-48 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        onKeyDown={(e) => { if (e.key === 'Enter') { handleDownloadPdf(); setShowPdfInput(false); } }}
                        autoFocus
                      />
                      <span className="text-xs text-gray-400">.pdf</span>
                      <button
                        onClick={() => { handleDownloadPdf(); setShowPdfInput(false); }}
                        disabled={isGeneratingPdf}
                        className="whitespace-nowrap rounded-lg bg-primary px-3 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                      >
                        생성
                      </button>
                    </div>
                  )}
                </div>

                {/* 인쇄 */}
                <button onClick={handlePrint} className="mr-2 rounded-full p-1 hover:bg-gray-200" title="인쇄">
                  <Printer size={20} />
                </button>
              </>
            )}

            {/* 닫기 */}
            <button onClick={onClose} className="rounded-full border border-gray-300 p-1 hover:bg-gray-200">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* ───── 컨텐츠 ───── */}
        <div className="flex-1 overflow-auto">
          {step === 'select' && (
            <div className="p-6">
              {/* Depth 선택 */}
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">단위 선택</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(d => (
                    <button
                      key={d}
                      onClick={() => setSelectedDepth(d)}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                        selectedDepth === d
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {depthTitles[d]}
                    </button>
                  ))}
                </div>
              </div>

              {/* 전체 선택 + 문제 수 */}
              <div className="mb-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={depthItems.length > 0 && checkedItems.size === depthItems.length}
                    onChange={handleSelectAll}
                    className="size-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="font-medium">전체 선택</span>
                  <span className="text-gray-500">({depthItems.length}개)</span>
                </label>
                <span className="text-sm text-gray-600">
                  선택된 문제: <span className="font-bold text-primary">{totalSelectedProblems}</span>개
                </span>
              </div>

              {/* 체크리스트 */}
              <div className="max-h-[calc(90vh-280px)] overflow-auto rounded-xl border border-gray-200">
                {depthItems.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">데이터가 없습니다.</div>
                ) : (
                  depthItems.map(item => (
                    <label
                      key={item.pathId}
                      className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 hover:bg-gray-50 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={checkedItems.has(item.pathId)}
                        onChange={() => handleCheck(item.pathId)}
                        className="size-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="truncate text-xs text-gray-500">{item.label}</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                        item.problemCount > 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {item.problemCount}문제
                      </span>
                    </label>
                  ))
                )}
              </div>

              {/* 미리보기 버튼 */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={fetchAndPrepareProblems}
                  disabled={checkedItems.size === 0 || totalSelectedProblems === 0}
                  className="rounded-3xl bg-primary px-6 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  미리보기 ({totalSelectedProblems}문제)
                </button>
              </div>
            </div>
          )}

          {step === 'loading' && (
            <div className="flex flex-1 items-center justify-center p-12">
              <div className="text-center">
                <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-primary"></div>
                <p className="text-gray-600">{loadingProgress}</p>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="bg-gray-200 p-5" style={{ minHeight: '100%' }}>
              <style>{`
                .tr-preview-container {
                  zoom: ${printPreviewScale};
                  margin: 0 auto;
                  width: fit-content;
                }
                .tr-page {
                  width: 328mm;
                  min-height: 464mm;
                  background: white;
                  box-sizing: border-box;
                  padding: 0;
                  position: relative;
                  margin-bottom: 20px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                }
                /* ── 모든 페이지 헤더: 기존 두 번째 페이지 이후와 동일 ── */
                .print-header-rest {
                  height: 15mm;
                  background: none;
                  margin: 0;
                  position: relative;
                }
                .tr-header-logo {
                  position: absolute;
                  top: 11mm;
                  width: auto;
                  height: 8mm;
                  object-fit: contain;
                  z-index: 2;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .tr-header-logo.left {
                  left: 16mm;
                }
                .tr-header-logo.right {
                  right: 16mm;
                }
                /* ── 문제 컨테이너: 모든 페이지 동일 (410.5mm) ── */
                .problems-container {
                  height: 410.5mm !important;
                  min-height: 410.5mm !important;
                  max-height: 410.5mm !important;
                  margin-top: 57px !important;
                  padding: 0 60.4724px !important;
                }
                .problems-container {
                  display: flex !important;
                  flex-direction: row !important;
                  justify-content: space-between !important;
                  gap: 60.4724px !important;
                  position: relative !important;
                  box-sizing: border-box !important;
                }
                .problems-container::after {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 50%;
                  height: 409mm;
                  width: 1px;
                  background-color: #ccc;
                  transform: translateX(-50%);
                }
                .problems-column {
                  flex: 1;
                  position: relative;
                  display: block;
                  width: calc(50% - 8mm);
                  min-height: 410.5mm;
                  overflow: visible;
                }
                .tr-problem-wrapper {
                  width: 100%;
                  position: absolute;
                  left: 0;
                }
                .tr-address-label {
                  font-size: 10px;
                  color: #666;
                  background: #F8F8F8;
                  padding: 4mm;
                  border-radius: 1rem;
                  display: flex;
                  flex-direction: row;
                  align-items: stretch;
                  gap: 7mm;
                  width: 100%;
                  height: 97px;
                  box-sizing: border-box;
                  margin-bottom: 6mm;
                  overflow: hidden;
                }
                .difficulty-section {
                  display: grid;
                  grid-template-rows: auto 1fr;
                  gap: 4px;
                  align-items: stretch;
                  justify-items: center;
                  height: 100%;
                  flex-shrink: 0;
                }
                .difficulty-bars {
                  display: flex;
                  flex-direction: column-reverse;
                  justify-content: flex-end;
                  gap: 3px;
                }
                .difficulty-bar {
                  width: 32px;
                  height: 7.5px;
                  border-radius: 1.5rem;
                  background: #D4F0E3;
                }
                .difficulty-bar.active {
                  background: #4ABC85;
                }
                .address-grid {
                  display: grid;
                  grid-template-columns: minmax(30%, auto) minmax(30%, auto);
                  grid-template-rows: 1fr 1fr;
                  align-items: stretch;
                  gap: 8px 6px;
                  height: 100%;
                  flex: 1;
                }
                .address-cell {
                  display: flex;
                  flex-direction: column;
                  gap: 0px;
                }
                .address-cell:nth-child(n+3) {
                  justify-content: flex-end;
                }
                .address-title {
                  font-size: 8px;
                  font-weight: bold;
                  color: #A3A1A2;
                  white-space: nowrap;
                }
                .address-data {
                  font-size: 12px;
                  font-weight: bold;
                  color: #231F20;
                  word-break: keep-all;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  padding-top: 2px;
                }
                .address-data .katex {
                  font-size: inherit;
                }
                .problem-item {
                  width: 100%;
                  max-width: 488px;
                  overflow-wrap: break-word;
                  text-align: left;
                  display: flex;
                  gap: 28px;
                  align-items: flex-start;
                  position: absolute;
                  left: 0;
                }
                .problem-number {
                  font-size: 36px;
                  font-weight: 900;
                  min-width: 36px;
                  white-space: nowrap;
                  color: #4ABC85;
                  margin: 0;
                  line-height: 1;
                  position: relative;
                }
                .problem-content {
                  margin: 0;
                  width: 100%;
                  overflow-wrap: break-word;
                  word-break: break-word;
                }
                .problem-content *:not(.katex):not(.katex *) {
                  font-family: 'Pretendard', sans-serif !important;
                  font-weight: 400 !important;
                  font-size: 18px !important;
                }
                .katex {
                  font-size: 1em !important;
                }
                .editor-paragraph { position: relative; }
                .editor-text-bold { font-weight: bold; }
                .editor-text-italic { font-style: italic; }
                .editor-text-underline { text-decoration: underline; }
                .PlaygroundEditorTheme__paragraph { margin: 0; position: relative; }
                .PlaygroundEditorTheme__text-bold { font-weight: bold; }
                .PlaygroundEditorTheme__boxContent {
                  border: 1px solid black;
                  padding: 10px;
                  margin: 10px auto;
                  max-width: 100%;
                  border-radius: 4px;
                  background-color: #ffffff;
                  page-break-inside: avoid;
                }
                .problem-content .PlaygroundEditorTheme__layoutContainer {
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 16mm;
                  max-width: 100%;
                  margin: 8mm 0;
                  page-break-inside: avoid;
                }
                .problem-content .PlaygroundEditorTheme__layoutItem {
                  width: 100%;
                  min-width: 0;
                  padding: 0;
                  box-sizing: border-box;
                  overflow-wrap: break-word;
                  border: none !important;
                  page-break-inside: avoid;
                }
                .PlaygroundEditorTheme__layoutItem > * {
                  margin: 2mm 0;
                }
                .problem-content img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                  margin: 8mm auto;
                }
                .problem-content .math-element {
                  font-size: 0.91em;
                  display: inline-block;
                  vertical-align: middle;
                  margin: 0 2px;
                }
                .PlaygroundEditorTheme__imageContainer {
                  text-align: center;
                  margin: 8mm 0;
                }
                .PlaygroundEditorTheme__image {
                  display: inline-block;
                  max-width: 100%;
                  vertical-align: middle;
                }
                .problem-content [data-lexical-layout-container] {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  grid-template-rows: repeat(3, auto);
                  max-width: 100%;
                }
                .problem-item[data-full-column="true"] {
                  height: 150mm;
                  z-index: 1;
                }
                .print-footer {
                  position: absolute;
                  bottom: 10mm;
                  left: 16mm;
                  right: 16mm;
                  padding-bottom: 0;
                }
                @media print {
                  .tr-page {
                    margin-bottom: 0;
                    box-shadow: none;
                    page-break-after: always;
                    page-break-inside: avoid;
                  }
                }
              `}</style>
              <div className="tr-preview-container" ref={previewContainerRef}>
                {pages.map((page, pageIndex) => (
                  <div key={pageIndex} className="tr-page">
                    {/* Header */}
                    <div className="print-header-rest"></div>
                    {/* 로고: 홀수페이지 좌측, 짝수페이지 우측 - 페이지 기준 absolute */}
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/images/Ky_logo_mini.png`}
                      alt=""
                      className={`tr-header-logo ${pageIndex % 2 === 0 ? 'left' : 'right'}`}
                    />

                    {/* Problems Container */}
                    <div className="problems-container">
                      {/* Left Column */}
                      <div className="problems-column">
                        {page.left?.map((problem) => (
                          <div
                            key={problem.problem_id}
                            className="tr-problem-wrapper"
                            style={{ top: `${problem.startHeight}mm` }}
                            data-full-column={problem.useFullColumn || false}
                          >
                            <div className="tr-address-label">
                              <div className="difficulty-section">
                                <span className="address-title">난이도</span>
                                <div className="difficulty-bars">
                                  {[1, 2, 3, 4, 5].map((level) => (
                                    <div key={level} className={`difficulty-bar${level <= problem.step ? ' active' : ''}`} />
                                  ))}
                                </div>
                              </div>
                              <div className="address-grid">
                                <div className="address-cell">
                                  <span className="address-title">대단원</span>
                                  <span className="address-data" style={{ color: '#4ABC85' }}><MathText text={problem.addressLabel[0] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">대유형</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[2] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">소단원</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[1] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">중유형</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[3] || ''} /></span>
                                </div>
                              </div>
                            </div>
                            <div className="problem-item" style={{ position: 'static' }}>
                              <div className="problem-number">
                                {String(problem.problemNumber).padStart(2, '0')}
                              </div>
                              <div
                                className="problem-content"
                                dangerouslySetInnerHTML={{ __html: problem.content }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right Column */}
                      <div className="problems-column">
                        {page.right?.map((problem) => (
                          <div
                            key={problem.problem_id}
                            className="tr-problem-wrapper"
                            style={{ top: `${problem.startHeight}mm` }}
                            data-full-column={problem.useFullColumn || false}
                          >
                            <div className="tr-address-label">
                              <div className="difficulty-section">
                                <span className="address-title">난이도</span>
                                <div className="difficulty-bars">
                                  {[1, 2, 3, 4, 5].map((level) => (
                                    <div key={level} className={`difficulty-bar${level <= problem.step ? ' active' : ''}`} />
                                  ))}
                                </div>
                              </div>
                              <div className="address-grid">
                                <div className="address-cell">
                                  <span className="address-title">대단원</span>
                                  <span className="address-data" style={{ color: '#4ABC85' }}><MathText text={problem.addressLabel[0] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">대유형</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[2] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">소단원</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[1] || ''} /></span>
                                </div>
                                <div className="address-cell">
                                  <span className="address-title">중유형</span>
                                  <span className="address-data"><MathText text={problem.addressLabel[3] || ''} /></span>
                                </div>
                              </div>
                            </div>
                            <div className="problem-item" style={{ position: 'static' }}>
                              <div className="problem-number">
                                {String(problem.problemNumber).padStart(2, '0')}
                              </div>
                              <div
                                className="problem-content"
                                dangerouslySetInnerHTML={{ __html: problem.content }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="print-footer" style={{
                      position: 'absolute',
                      bottom: '10mm',
                      left: 0,
                      right: 0,
                      width: '100%',
                      height: '15mm',
                      padding: '0 15mm',
                      boxSizing: 'border-box'
                    }}>
                      <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0 0 5mm 0', width: '100%' }} />
                      <div style={{ position: 'relative', height: '8mm', display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: '#666',
                          fontSize: '10pt',
                          width: '100%',
                          fontFamily: "'Pretendard', sans-serif"
                        }}>
                          <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>
                            {treeData?.field_info?.edu_ver || ''}
                          </span>
                          <span style={{ fontWeight: 'bold', fontFamily: "'Pretendard', sans-serif" }}>
                            {treeData?.field_info?.tree_name || ''}
                          </span>
                        </div>
                        <span style={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '18pt',
                          fontWeight: 'bold',
                          lineHeight: '1',
                          fontFamily: "'Pretendard', sans-serif"
                        }}>
                          {String(pageIndex + 1).padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TestRegisterPrintModal;
